import { type BaseQueryFn, createApi } from '@reduxjs/toolkit/query/react';
import { type AxiosError, type AxiosInstance, type AxiosRequestConfig } from 'axios';
import { apiClient } from 'src/clients/api-client';
import { logAxiosError } from './log-axios-error';

export const createBaseQuery = (
  client: AxiosInstance,
  options?: {
    additionalErrorHandling: (error: AxiosError) => void;
  }): BaseQueryFn<
{
  url: string;
  method?: AxiosRequestConfig['method'];
  body?: AxiosRequestConfig['data']; // RTK uses `body`; Axios uses `data`.
  params?: AxiosRequestConfig['params'];
},
unknown,
unknown
> =>
  async ({ url, method, body: data, params }) => {
    try {
      const result = await client({
        url,
        method,
        data,
        params,
      });
      return { data: result.data };
    } catch (axiosError) {
      const err = axiosError as AxiosError;

      if (err.response && err.response.status >= 500) {
        logAxiosError(err);
      }
      options?.additionalErrorHandling(err);
      return {
        error: {
          status: err.response?.status,
          data: err.response?.data ?? err.message,
        },
      };
    }
  };

// Initialize an empty api service that we'll inject endpoints into via the rtk-query OpenAPI code generator
export const rtkQueryBase = createApi({
  baseQuery: createBaseQuery(apiClient),
  endpoints: () => ({}),
});
